import { useMemo, useState } from "react";

import type {
  SettlementTreatmentEnum,
  TriageSuggestionSettlementQuery,
} from "@/__generated__/graphql";
import { AnimatedTabs } from "@/components/atoms/AnimatedTabs";
import { Queries } from "@/constants";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

import {
  TriageJournalEntryTable,
  type TriageJournalEntryLine,
  type TriageJournalSource,
} from "./TriageJournalEntryPanel";

/**
 * A payment one business made for another - or took in for it - books in both:
 * the payer's bank against what is owed between them, and the other's side of
 * what the money was for. How it is owed is the person's to say when they take
 * it: a loan, the owner's money, or the payer's own after all. Each way comes
 * from the server with the entries it books, so what is shown is what is booked.
 */

export type TriageSettlementTreatment = NonNullable<
  Extract<
    NonNullable<TriageSuggestionSettlementQuery["node"]>,
    { __typename?: "RuleSuggestionObject" }
  >["settlementTreatments"]
>[number];

type SettlementLine = TriageSettlementTreatment["lines"][number];

/** Wide enough for each business's entry to have the room of one on its own. */
const SIDE_BY_SIDE_MIN_WIDTH = 720;

/** Who the entries are for while they load. */
const PLACEHOLDER_BUSINESSES = [
  {
    __typename: "EntityObject",
    id: "payer",
    identifier: "payer",
    name: "Northwind Traders",
  },
  {
    __typename: "EntityObject",
    id: "other",
    identifier: "other",
    name: "Harbour Supply",
  },
] as SettlementLine["entity"][];

/** How a suggestion booking in two businesses can be settled, each way with its entries. */
export function useTriageSettlementTreatments(suggestionId: string | null) {
  const { data, loading } = useBackendQuery(
    Queries.TRIAGE_SUGGESTION_SETTLEMENT_QUERY,
    { variables: { id: suggestionId ?? "" }, skip: suggestionId == null },
  );
  const treatments =
    data?.node?.__typename === "RuleSuggestionObject"
      ? data.node.settlementTreatments
      : [];
  return { treatments, isLoading: loading || data === undefined };
}

/**
 * The entries a settlement books, one per business, each the preview's own
 * journal table. Side by side when there is room, read across like a diff: the
 * two banks share a row, and so do the two sides of what is owed between them;
 * each other line has a blank row opposite it. Narrower, a tab per business.
 */
export function TriageSettlementEntries({
  lines,
  payerIdentifier,
  isLoading = false,
}: {
  lines: SettlementLine[];
  payerIdentifier: string;
  isLoading?: boolean;
}) {
  const [width, setWidth] = useState<number | null>(null);
  const measured = useResizeObserver((rect) => setWidth(rect.width));
  const sideBySide = width == null || width >= SIDE_BY_SIDE_MIN_WIDTH;

  const businesses = useMemo(() => {
    if (isLoading) return PLACEHOLDER_BUSINESSES;
    const payer = lines.find(
      (line) => line.entity.identifier === payerIdentifier,
    );
    const other = lines.find(
      (line) => line.entity.identifier !== payerIdentifier,
    );
    return [payer?.entity, other?.entity].filter(
      (entity): entity is SettlementLine["entity"] => entity != null,
    );
  }, [isLoading, lines, payerIdentifier]);
  const [tab, setTab] = useState<string | null>(null);
  const shownTab = tab ?? businesses[0]?.identifier ?? "";

  // Row by row across the two: the same money on both sides shares a row.
  const rows = useMemo(() => {
    const theirs = (identifier: string | undefined, role: string) =>
      lines.filter(
        (line) => line.entity.identifier === identifier && line.role === role,
      );
    const [payer, other] = businesses.map((entity) => entity.identifier);
    const across: Array<[SettlementLine | null, SettlementLine | null]> = [];
    for (const role of ["bank", "entry", "settlement"]) {
      const left = theirs(payer, role);
      const right = theirs(other, role);
      if (role === "entry") {
        left.forEach((line) => across.push([line, null]));
        right.forEach((line) => across.push([null, line]));
        continue;
      }
      for (
        let index = 0;
        index < Math.max(left.length, right.length);
        index++
      ) {
        across.push([left[index] ?? null, right[index] ?? null]);
      }
    }
    return across;
  }, [businesses, lines]);

  const entry = (side: number, aligned: boolean) => {
    const business = businesses[side];
    const sideLines = aligned
      ? rows.map((row) => row[side])
      : lines.filter((line) => line.entity.identifier === business?.identifier);
    return (
      <TriageJournalEntryTable
        triage={{ entity: business } as TriageJournalSource}
        isLoading={isLoading}
        lines={
          isLoading
            ? []
            : sideLines.map((line, index): TriageJournalEntryLine => {
                const amount = Number(line?.amount ?? 0);
                return line == null
                  ? // Nothing of this business's opposite the other's line.
                    {
                      description: "",
                      debit: null,
                      credit: null,
                      chartedAccount: { name: "" },
                      metadata: { _frontend_uuid: `blank-${side}-${index}` },
                    }
                  : {
                      description: line.description,
                      debit: amount > 0 ? amount : 0,
                      credit: amount < 0 ? -amount : 0,
                      // One the answer makes is named as the catalogue's are
                      // - the cell draws a reference only with an id.
                      chartedAccount: line.chartedAccount
                        ? { ...line.chartedAccount, name: line.accountName }
                        : {
                            id: identifierAndTypenameToId(
                              `new-${line.accountName}`,
                              "ChartedAccountObject",
                            ),
                            identifier: null,
                            name: `${line.accountName} (new account)`,
                          },
                      metadata: { _frontend_uuid: `line-${side}-${index}` },
                    };
              })
        }
      />
    );
  };

  return (
    <div
      ref={measured}
      data-testid="triage-settlement-entries"
      data-layout={sideBySide ? "side-by-side" : "tabs"}
      className="min-w-0"
    >
      {sideBySide ? (
        <div className="grid grid-cols-[1fr_1fr]">
          {businesses.map((business, side) => (
            <div
              key={business.identifier}
              data-testid="triage-settlement-side"
              data-entity={business.identifier}
              className={cn(
                "min-w-0",
                side === 1 &&
                  "[border-left-width:1px] [border-left-style:solid] border-border",
              )}
            >
              <Typography level="title-sm" className="pl-4 pr-4 pt-3 pb-2">
                <Skeleton loading={isLoading} variant="inline">
                  {business.name}
                </Skeleton>
              </Typography>
              {entry(side, true)}
            </div>
          ))}
        </div>
      ) : (
        <>
          <AnimatedTabs
            size="sm"
            align="start"
            tabs={businesses.map((business) => ({
              id: business.identifier,
              label: (
                <Skeleton loading={isLoading} variant="inline">
                  {business.name}
                </Skeleton>
              ),
            }))}
            selectedTabId={shownTab}
            onTabChange={setTab}
            rootClassName="pl-4 pr-4 pt-3 pb-2"
          />
          <div
            data-testid="triage-settlement-side"
            data-entity={shownTab}
            className="min-w-0"
          >
            {entry(
              Math.max(
                businesses.findIndex(
                  (business) => business.identifier === shownTab,
                ),
                0,
              ),
              false,
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The entries a suggestion booking in two businesses would book, the way the
 * person has said they settle it - a loan until they say otherwise.
 */
export function TriageSettlementPreview({
  suggestionId,
  payerIdentifier,
  treatment,
}: {
  suggestionId: string;
  payerIdentifier: string;
  treatment: SettlementTreatmentEnum | null;
}) {
  const { treatments, isLoading } = useTriageSettlementTreatments(suggestionId);
  const chosen =
    treatments.find((option) => option.treatment === treatment) ??
    treatments[0];
  return (
    <TriageSettlementEntries
      lines={chosen?.lines ?? []}
      payerIdentifier={payerIdentifier}
      isLoading={isLoading}
    />
  );
}

/**
 * What the person is asked when they accept a payment made for another
 * business: was it a loan, or an expense - and if an expense, which of the
 * common ways it is booked, each said plainly, one recommended.
 */
export function TriageSettlementChoice({
  treatments,
  treatment,
  onChange,
}: {
  treatments: TriageSettlementTreatment[];
  treatment: SettlementTreatmentEnum | null;
  onChange: (treatment: SettlementTreatmentEnum) => void;
}) {
  const loan = treatments.find((option) => option.isLoan);
  const expenses = treatments.filter((option) => !option.isLoan);
  const chosen = treatments.find((option) => option.treatment === treatment);
  const kind = chosen == null ? null : chosen.isLoan ? "loan" : "expense";
  const recommended =
    expenses.find((option) => option.isRecommended) ?? expenses[0];

  return (
    <div data-testid="triage-settlement-choice" className="flex flex-col gap-3">
      <RadioGroup
        value={kind}
        onValueChange={(value) => {
          const picked =
            value === "loan"
              ? loan
              : chosen?.isLoan === false
                ? chosen
                : recommended;
          if (picked) onChange(picked.treatment);
        }}
        className="gap-2"
      >
        <Label className="flex items-center gap-2">
          <RadioGroupItem
            value="loan"
            disabled={loan == null}
            data-testid="triage-settlement-loan"
          />
          A loan
        </Label>
        {kind === "loan" && loan ? (
          <Typography level="body-sm" className="pl-6 text-muted-foreground">
            {loan.description}
          </Typography>
        ) : null}
        <Label className="flex items-center gap-2">
          <RadioGroupItem
            value="expense"
            disabled={expenses.length === 0}
            data-testid="triage-settlement-expense"
          />
          An expense
        </Label>
      </RadioGroup>
      {kind === "expense" ? (
        // Nobody pays it back: which of the usual ways it is booked.
        <RadioGroup
          value={treatment ?? ""}
          onValueChange={(value) => onChange(value as SettlementTreatmentEnum)}
          className="pl-6 gap-2.5"
        >
          {expenses.map((option) => (
            <div
              key={option.treatment}
              data-testid="triage-settlement-expense-option"
            >
              <Label className="flex items-center gap-2">
                <RadioGroupItem
                  value={option.treatment}
                  data-testid={`triage-settlement-${option.treatment}`}
                />
                {option.title}
                {option.isRecommended ? (
                  <Badge variant="secondary">Recommended</Badge>
                ) : null}
              </Label>
              <Typography
                level="body-sm"
                className="pl-6 pt-0.5 text-muted-foreground"
              >
                {option.description}
              </Typography>
            </div>
          ))}
        </RadioGroup>
      ) : null}
    </div>
  );
}
